import { useMemo, useRef, useState } from "react";
import { useRoute, Link } from "wouter";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Check, Minus, Package, Plus, ShoppingCart } from "lucide-react";
import { flyToCart } from "@/lib/fly-to-cart";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatKD } from "@/lib/utils";
import { useProduct, useProducts } from "@/hooks/use-products";
import { useShopCart } from "@/lib/cart";
import { useShopMarket } from "@/lib/shop-market";
import { useToast } from "@/hooks/use-toast";
import { ProductCard } from "@/components/ProductCard";

export default function ProductDetails() {
  // Same page for /shop/:slug and the B2B market's /b2b/:key/:slug.
  const [, retailParams] = useRoute("/shop/:slug");
  const [, b2bParams] = useRoute("/b2b/:key/:slug");
  const slug = retailParams?.slug ?? b2bParams?.slug;
  const { b2bKey, basePath } = useShopMarket();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { data: product, isLoading } = useProduct(slug, b2bKey);
  const { data: allProducts } = useProducts(b2bKey);
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useShopCart();
  const { toast } = useToast();
  const [justAdded, setJustAdded] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  const related = useMemo(() => {
    if (!product || !allProducts) return [];
    return allProducts
      .filter((p) => p.categoryId === product.categoryId && p.id !== product.id)
      .slice(0, 3);
  }, [product, allProducts]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10">
            <div className="aspect-square rounded-3xl bg-muted animate-pulse" />
            <div className="space-y-4 pt-4">
              <div className="h-8 w-2/3 bg-muted animate-pulse rounded" />
              <div className="h-5 w-1/3 bg-muted animate-pulse rounded" />
              <div className="h-24 w-full bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 pb-24 flex items-center justify-center">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
          <p className="text-lg font-semibold mb-2">{t("shop.notFound")}</p>
          <Link href={basePath}>
            <Button variant="outline" className="rounded-full mt-2">
              {t("shop.backToShop")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const name = isRtl ? product.nameAr : product.nameEn;
  const category = isRtl ? product.categoryNameAr : product.categoryNameEn;
  const description = isRtl ? product.descriptionAr : product.descriptionEn;
  const features = isRtl ? product.featuresAr : product.featuresEn;
  const maxQty = Math.max(1, product.stockQuantity);
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen pt-24 pb-24">
      <div className="container mx-auto px-4">
        <Link
          href={basePath}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          data-testid="back-to-shop"
        >
          <BackArrow className="w-4 h-4" />
          {t("shop.backToShop")}
        </Link>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* image */}
          <div ref={imageRef} className="relative isolate rounded-3xl border border-border overflow-hidden bg-card">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt={name} className="w-full aspect-square object-cover" />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center bg-muted">
                <Package className="h-20 w-20 text-muted-foreground/30" />
              </div>
            )}
            {!product.inStock && (
              <span className="absolute top-4 start-4 rounded-full bg-destructive/90 text-destructive-foreground text-xs font-semibold px-3 py-1.5">
                {t("shop.outOfStock")}
              </span>
            )}
          </div>

          {/* info */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="u-eyebrow text-secondary">{category}</span>
              {product.brand && (
                <Badge variant="outline" className="rounded-full">{product.brand}</Badge>
              )}
              {product.size && (
                <Badge variant="outline" className="rounded-full">{product.size}</Badge>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold font-display mb-3">{name}</h1>
            <p className="flex items-baseline gap-1.5 mb-6">
              <span className="text-3xl font-bold font-display text-primary">
                {formatKD(product.price)}
              </span>
              <span className="text-sm text-muted-foreground">{t("services.currency")}</span>
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">{description}</p>

            {features.length > 0 && (
              <ul className="space-y-2.5 mb-8">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Check className="h-3 w-3 text-primary" />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
            )}

            {/* qty + add to cart (cart wiring lands with the checkout phase) */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center rounded-full border border-border">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full"
                  disabled={!product.inStock || quantity <= 1}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  data-testid="qty-minus"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-10 text-center font-semibold" data-testid="qty-value">
                  {quantity}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full"
                  disabled={!product.inStock || quantity >= maxQty}
                  onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                  data-testid="qty-plus"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {product.inStock && product.stockQuantity <= 5 && (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  {t("shop.lowStock", { count: product.stockQuantity })}
                </span>
              )}
            </div>
            <Button
              className="w-full h-14 text-lg font-semibold rounded-xl"
              disabled={!product.inStock}
              onClick={() => {
                addItem(product, quantity, b2bKey);
                flyToCart(imageRef.current, product.imageUrl);
                setJustAdded(true);
                setTimeout(() => setJustAdded(false), 1500);
              }}
              data-testid="add-to-cart"
            >
              {justAdded ? <Check className="w-5 h-5 me-2" /> : <ShoppingCart className="w-5 h-5 me-2" />}
              {justAdded
                ? t("shop.addedToCart")
                : product.inStock
                  ? t("shop.addToCart")
                  : t("shop.outOfStock")}
            </Button>
          </div>
        </div>

        {/* related */}
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="text-2xl font-bold font-display mb-6">{t("shop.related")}</h2>
            <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
